import { Controller } from "@hotwired/stimulus"
import { speakWithLipSync } from "@primeta/persona-core"

// Floating hero avatar — rotates through library personas, plays intros on click.
// Uses stored phonemes + speakWithLipSync for natural mouth movement.
export default class extends Controller {
  static targets = ["avatar", "speakButton"]
  static values = { personas: Array }

  connect() {
    this._index = 0
    this._audioCtx = null
    this._currentSpeech = null

    // Listen for avatar ready to hide loader
    this.element.addEventListener('avatar:ready', () => this._hideLoader())
  }

  disconnect() {
    this._stopSpeaking()
  }

  async play(event) {
    event?.stopPropagation?.()
    const persona = this.personasValue[this._index]
    if (!persona?.sampleUrl) return

    // If already speaking, stop
    if (this._currentSpeech) {
      this._stopSpeaking()
      return
    }

    const ctrl = this._avatarController()
    const vrm = ctrl?._avatar?.vrm
    if (!vrm) return

    if (!this._audioCtx) {
      this._audioCtx = new (window.AudioContext || window.webkitAudioContext)()
    }
    if (this._audioCtx.state === 'suspended' || this._audioCtx.state === 'interrupted') {
      await this._audioCtx.resume()
    }

    // iOS silent mode bypass: play a silent buffer through the AudioContext
    // during this user gesture to switch from "ambient" to "playback" audio session.
    if (!window._iosAudioUnlocked && this._audioCtx.state === 'running') {
      try {
        const silentBuffer = this._audioCtx.createBuffer(1, this._audioCtx.sampleRate * 0.25, this._audioCtx.sampleRate)
        const src = this._audioCtx.createBufferSource()
        src.buffer = silentBuffer
        src.connect(this._audioCtx.destination)
        src.start()

        const audio = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAAbAAqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dX/////////////////////////////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+M4xAANaALkAUAAAP/jOMQJDSgCgBQBAAAAAAAAAAAAAAAAAAA=')
        audio.play().catch(() => {})

        window._iosAudioUnlocked = true
      } catch {}
    }

    try {
      const res = await fetch(persona.sampleUrl)
      const audioBuffer = await res.arrayBuffer()

      this._setSpeakingState(true)

      if (persona.phonemes && persona.phonemes.length > 0) {
        this._currentSpeech = speakWithLipSync({
          audioBuffer,
          phonemes: persona.phonemes,
          vrm,
          avatar: ctrl?._avatar,
          audioCtx: this._audioCtx,
          playAnimation: (name) => ctrl?.playAnimation?.(`__${name}`),
          onDone: () => { this._currentSpeech = null; this._setSpeakingState(false) }
        })
      } else {
        await this._playWithAmplitudeLipSync(audioBuffer, vrm, ctrl)
      }
    } catch (err) {
      console.error("[hero-avatar] play error", err)
      this._stopSpeaking()
    }
  }

  async _playWithAmplitudeLipSync(arrayBuffer, vrm, ctrl) {
    // Use <audio> element instead of AudioBufferSourceNode to bypass iOS silent mode
    const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' })
    const blobUrl = URL.createObjectURL(blob)
    const audio = new Audio(blobUrl)
    const sourceNode = this._audioCtx.createMediaElementSource(audio)
    sourceNode.connect(this._audioCtx.destination)

    const stopLipSync = this._startAmplitudeLipSync(sourceNode, vrm, ctrl?._avatar)
    ctrl?.playAnimation?.("__talking")

    audio.onended = () => {
      stopLipSync?.()
      URL.revokeObjectURL(blobUrl)
      ctrl?.playAnimation?.("__idle")
      this._currentSpeech = null
      this._setSpeakingState(false)
    }
    await audio.play()
    this._currentSpeech = {
      cancel: () => {
        audio.pause()
        audio.src = ''
        URL.revokeObjectURL(blobUrl)
        stopLipSync?.()
        ctrl?.playAnimation?.("__idle")
        this._currentSpeech = null
      }
    }
  }

  _startAmplitudeLipSync(sourceNode, vrm, avatar) {
    if (!vrm?.expressionManager) return null
    if (avatar?.facial) avatar.facial.suppressMouthOverride = true

    const analyser = this._audioCtx.createAnalyser()
    analyser.fftSize = 1024
    analyser.smoothingTimeConstant = 0.85
    sourceNode.connect(analyser)
    const data = new Uint8Array(analyser.frequencyBinCount)

    const has = (n) => { try { return vrm.expressionManager.getExpression(n) != null } catch { return false } }
    const resolve = (lc, uc) => has(lc) ? lc : (has(uc) ? uc : null)
    const names = {
      aa: resolve('aa', 'A'),
      ih: resolve('ih', 'I'),
      oh: resolve('oh', 'O'),
    }
    if (!names.aa && !names.ih && !names.oh) {
      if (avatar?.facial) avatar.facial.suppressMouthOverride = false
      return null
    }

    const binHz = this._audioCtx.sampleRate / analyser.fftSize
    const bandSum = (lo, hi) => {
      const l = Math.max(1, Math.floor(lo / binHz))
      const h = Math.min(data.length - 1, Math.ceil(hi / binHz))
      let s = 0, n = 0
      for (let i = l; i <= h; i++) { s += data[i]; n++ }
      return n > 0 ? (s / n) / 255 : 0
    }

    let rafId
    const current = { aa: 0, ih: 0, oh: 0 }
    const SMOOTH = 0.18

    const loop = () => {
      analyser.getByteFrequencyData(data)
      const lowE = bandSum(80, 500)
      const midE = bandSum(500, 1500)
      const hiE  = bandSum(1500, 4000)
      const overall = Math.min(1, (lowE + midE + hiE) * 1.4)

      if (overall < 0.05) {
        current.aa *= 0.6; current.ih *= 0.6; current.oh *= 0.6
      } else {
        const total = lowE + midE + hiE + 0.0001
        current.aa += ((midE / total) * overall - current.aa) * SMOOTH
        current.ih += ((hiE / total) * overall * 0.8 - current.ih) * SMOOTH
        current.oh += ((lowE / total) * overall * 0.7 - current.oh) * SMOOTH
      }

      try {
        if (names.aa) vrm.expressionManager.setValue(names.aa, current.aa < 0.01 ? 0 : current.aa)
        if (names.ih) vrm.expressionManager.setValue(names.ih, current.ih < 0.01 ? 0 : current.ih)
        if (names.oh) vrm.expressionManager.setValue(names.oh, current.oh < 0.01 ? 0 : current.oh)
      } catch {}
      rafId = requestAnimationFrame(loop)
    }
    rafId = requestAnimationFrame(loop)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      try {
        if (names.aa) vrm.expressionManager.setValue(names.aa, 0)
        if (names.ih) vrm.expressionManager.setValue(names.ih, 0)
        if (names.oh) vrm.expressionManager.setValue(names.oh, 0)
      } catch {}
      try { analyser.disconnect() } catch {}
      if (avatar?.facial) avatar.facial.suppressMouthOverride = false
    }
  }

  next(event) {
    event?.stopPropagation?.()
    this._navigate(1)
  }

  prev(event) {
    event?.stopPropagation?.()
    this._navigate(-1)
  }

  _stopSpeaking() {
    this._currentSpeech?.cancel()
    this._currentSpeech = null
    this._setSpeakingState(false)
  }

  _setSpeakingState(speaking) {
    if (!this.hasSpeakButtonTarget) return
    const btn = this.speakButtonTarget
    if (speaking) {
      btn.innerHTML = `<span class="speaking-dots">Speaking</span>`
      btn.classList.add('pointer-events-auto')
    } else {
      btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" /></svg> Speak`
    }
  }

  _navigate(direction) {
    if (this.personasValue.length < 2) return

    this._stopSpeaking()
    this._audio?.pause()
    this._audio = null
    this._index = (this._index + direction + this.personasValue.length) % this.personasValue.length
    const persona = this.personasValue[this._index]

    const el = this.hasAvatarTarget ? this.avatarTarget : this.element.querySelector("[data-controller~='avatar']")
    if (!el) return

    this._showLoader()
    el.dataset.avatarAssetsUrlValue = persona.assetsUrl
  }

  _showLoader() {
    if (this._loader) return
    this._loader = document.createElement('div')
    this._loader.className = 'absolute inset-0 z-30 flex items-center justify-center bg-bg/60 backdrop-blur-sm transition-opacity'
    this._loader.innerHTML = `<span class="text-xs text-fg-muted" style="font-family: var(--font-mono);">Loading...</span>`
    this.element.querySelector('[data-avatar-target="canvas"]')?.parentElement?.appendChild(this._loader)
  }

  _hideLoader() {
    if (this._loader) {
      this._loader.remove()
      this._loader = null
    }
  }

  _avatarController() {
    const el = this.hasAvatarTarget ? this.avatarTarget : this.element.querySelector("[data-controller~='avatar']")
    return el?.avatarController
  }
}
